import React, { useContext, useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";

import {
  PostBillAPIContext,
  PostBillContext,
  UppAuthContext,
} from "@app/provider";
import { routes } from "@app/routing";
import { ButtonNoShadow } from "@app/components/buttonNoShadow";
import { AccountInfoBox } from "@app/preBill/components/accountInfoBox";
import SuccessInfoBox from "@app/postBill/components/successInfoBox";
import { SuccessInfoBox as PreBillSuccessInfoBox } from "@app/preBill/components/successInfoBox";
import useTokenizedLinkSignUp from "@app/postBill/hooks/useTokenizedLinkSignUp";
import { usePostBillHeapEvent } from "@app/postBill/hooks";
import { HEAP_EVENTS } from "@app/provider/types";
import { getLimsClinicIds, getTestsOrdered } from "@app/postBill/utils";
import SurveyModal from "@app/postBill/components/surveyModal";

import "./billingPaymentConfirmation.scss";

const messages = defineMessages({
  billingPaymentConfirmationTitle: {
    id: "billingPaymentConfirmationTitle",
    defaultMessage: "Thank you for your payment!",
  },
  billingPaymentConfirmationDescription: {
    id: "billingPaymentConfirmationDescription",
    defaultMessage:
      "We’ve received your payment. You’ll receive a confirmation email shortly.",
  },
  billingPaymentConfirmationDescriptionLoggedIn: {
    id: "billingPaymentConfirmationDescriptionLoggedIn",
    defaultMessage:
      "We’ve received your payment, but it may take a few days for the balance to update. You’ll receive a confirmation email shortly.",
  },
  billingPaymentPlanConfirmationTitle: {
    id: "billingPaymentPlanConfirmationTitle",
    defaultMessage: "Your payment plan is set up!",
  },
  billingPaymentPlanConfirmationDescription: {
    id: "billingPaymentPlanConfirmationDescription",
    defaultMessage:
      "We’ve received your request. You’ll get a confirmation email once your payment is fully processed—this may take a few minutes.",
  },
  billingPaymentConfirmationButtonLoggedIn: {
    id: "billingPaymentConfirmationButtonLoggedIn",
    defaultMessage: "Go to My Bills",
  },
});

enum BillingPaymentAction {
  PAYMENT = "payment",
  PAYMENT_PLAN = "payment_plan",
  PAY_FULL_BALANCE = "pay_full_balance",
}
interface BillingPaymentConfirmationLocationState {
  action?: BillingPaymentAction;
}

const BillingPaymentConfirmation: React.FC = () => {
  const intl = useIntl();
  const { profile: uppProfile } = useContext(UppAuthContext);
  const { redirectToTokenizedSignUp, isLoading } = useTokenizedLinkSignUp();
  const navigate = useNavigate();
  const { trackPostBillHeapEvent } = usePostBillHeapEvent();
  const { selectedBills, selectedPaymentPlan } = useContext(PostBillContext);
  const {
    apiProviderState: { billingData },
  } = useContext(PostBillAPIContext);
  const showCreateButton =
    !uppProfile?.patientUID && !billingData?.hasInternationalClinic;
  const location = useLocation();
  const [showSurvey, setShowSurvey] = useState(true);

  const locationState =
    (location.state as BillingPaymentConfirmationLocationState) ?? "";

  useEffect(() => {
    trackPostBillHeapEvent({
      eventKey: HEAP_EVENTS.upp_view_billing_payment_confirmation,
      extraData: {
        tests_ordered: getTestsOrdered(selectedBills),
        lims_clinic_id: getLimsClinicIds(selectedBills),
        pay_plan_length: selectedPaymentPlan,
      },
    });
  }, []);

  const handleCloseSurvey = () => {
    setShowSurvey(false);
  };

  const handleRedirection = () => navigate(routes.billingCenter);

  const renderSuccessInfoBox = () => {
    if (uppProfile?.patientUID) {
      return (
        <SuccessInfoBox
          title={intl.formatMessage(
            locationState.action === BillingPaymentAction.PAYMENT_PLAN
              ? messages.billingPaymentPlanConfirmationTitle
              : messages.billingPaymentConfirmationTitle
          )}
          description1={intl.formatMessage(
            locationState.action === BillingPaymentAction.PAYMENT_PLAN
              ? messages.billingPaymentPlanConfirmationDescription
              : messages.billingPaymentConfirmationDescriptionLoggedIn
          )}
        />
      );
    }
    return (
      <PreBillSuccessInfoBox
        title={intl.formatMessage(
          locationState.action === BillingPaymentAction.PAYMENT_PLAN
            ? messages.billingPaymentPlanConfirmationTitle
            : messages.billingPaymentConfirmationTitle
        )}
        description={intl.formatMessage(
          locationState.action === BillingPaymentAction.PAYMENT_PLAN
            ? messages.billingPaymentPlanConfirmationDescription
            : messages.billingPaymentConfirmationDescription
        )}
      />
    );
  };

  return (
    <div
      className={
        uppProfile?.patientUID
          ? "billing-payment-conclusion billing-payment-conclusion__logged-in"
          : "billing-payment-conclusion"
      }
    >
      <div className="billing-payment-conclusion__content">
        {renderSuccessInfoBox()}
        <ButtonNoShadow raised onClick={handleRedirection}>
          {intl.formatMessage(
            messages.billingPaymentConfirmationButtonLoggedIn
          )}
        </ButtonNoShadow>
        {showCreateButton && (
          <AccountInfoBox
            createAccountButtonAction={redirectToTokenizedSignUp}
            isLoading={isLoading}
            data-testid="account-info-box__create-account"
          />
        )}
      </div>
      {showSurvey && <SurveyModal onClose={handleCloseSurvey} />}
    </div>
  );
};

export default BillingPaymentConfirmation;
